import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../molecules/Button';
import { GoArrowLeft } from 'react-icons/go';
import iconDownload from '../../../assets/images/icon-download.svg';
import Field from '../Field';
import Form from '../../molecules/Form/Form';
import { FiSearch } from 'react-icons/fi';
import { useForm } from '../../molecules/Form';
import TableLayout from '../../molecules/TableLayout';
import Table from '../../molecules/Table';
import { TasksPreviewStyle } from './TasksPreviewSection.styles';
import FilterSelect from './FilterSelect';
import { useCsv } from '../../../Context/linkedinScrapperCsvContext';
import usePersistedSearchQuery from '../../../hooks/usePersistedSearchQuery';

const TasksPreviewSection = () => {
  const [form] = useForm();
  const { csvColumns, csvRows, csvLoading } = useCsv();
  const [filters, setFilters] = useState({});
  const [pageQuery, setPageQuery] = usePersistedSearchQuery({ page: 1 }, { paramKey: 'previewPage' });
  const currentPage = pageQuery.page;
  const setCurrentPage = page => setPageQuery(prev => ({ ...prev, page }));

  const pageSize = 14;

  const filterList = [
    { title: 'Current Position', options: ['Match', 'Not a Match'], isInput: false },
    { title: 'Match', options: ['Match', 'Not a Match'], isInput: false },
    { title: 'Duplicated in other lists', options: ['Is duplicate', 'Is not duplicate'], isInput: false },
    { title: 'Added to my Inbox', options: ['Is added', 'Is not added'], isInput: false },
    { title: 'Added to a Pipeline', options: ['Is added', 'Is not added'], isInput: false },
    {
      title: 'Connection / Conversation',
      options: ['Is a connection', 'Is not a connection', 'Has a conversation', 'Has not a conversation'],
      isInput: false,
    },
    {
      title: 'Enriched Email Address',
      options: ['Is not provided', 'Is provided', 'Contains', 'Not contains'],
      isInput: false,
    },
    { title: 'Scraped Email Address', options: ['Is provided', 'Is not provided'], isInput: false },
    { title: 'Phone Number', options: ['Is not provided', 'Is provided'], isInput: false },
    {
      title: 'LinkedIn account',
      options: [
        'Is open profile',
        'Is not open profile',
        'Has not profile picture',
        'Has profile picture',
        'Is open to work',
        'Is no open to work',
        'Is premium',
        'Is not premium',
      ],
      isInput: false,
    },
    { title: 'Language', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Job title', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Number of connections', options: ['Superior than', 'Inferior than'], isInput: true },
    { title: 'Skills', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Years in job', options: ['Superior than', 'Inferior than'], isInput: true },
    { title: 'Years in company', options: ['Superior than', 'Inferior than'], isInput: true },
    { title: 'Headline', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Rental', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Company name', options: ['Contains', 'Not contains'], isInput: true },
    { title: 'Company employees count', options: ['Superior than', 'Inferior than'], isInput: true },
    { title: 'Company founded since', options: ['Superior than', 'Inferior than'], isInput: true },
    { title: 'Company industry', options: ['Contains', 'Not contains'], isInput: true },
  ];

  const columnNames = csvColumns;

  // ✅ Helper: normalize value
  const normalize = val => {
    if (!val) return '';
    const str = String(val)
      .replace(/\r?\n|\r|\t/g, '')
      .trim(); // remove newlines/tabs
    return str === '------------' ? '' : str;
  };

  const findColumnKey = (title, csvColumns) => {
    if (!csvColumns) return null;
    const normalizedTitle = title.replace(/[\s_]+/g, '').toLowerCase(); // spaces & underscores
    return csvColumns.find(col => col.replace(/[\s_]+/g, '').toLowerCase() === normalizedTitle);
  };

  // ✅ Filtering logic
  const filteredRows = useMemo(() => {
    let rows = [...(csvRows || [])];

    // 🔍 Search filter
    const searchValue = form.getFieldValue('search');
    if (searchValue) {
      const lower = searchValue.toLowerCase();
      rows = rows.filter(row =>
        Object.values(row).some(val =>
          String(val || '')
            .toLowerCase()
            .includes(lower),
        ),
      );
    }

    // 🎯 Apply all filters dynamically
    Object.entries(filters).forEach(([filterName, selected]) => {
      if (!selected?.length) return; // no filter active

      const columnKey = findColumnKey(filterName, csvColumns);
      if (!columnKey) {
        console.warn(`⚠️ No column found for filter "${filterName}"`);
        return;
      }

      rows = rows.filter(row => {
        const rawValue = row[columnKey];
        const value = normalize(rawValue);

        for (const condition of selected) {
          switch (condition) {
            /** ✅ Generic cases */
            case 'Match':
              if (value === '') return false;
              break;
            case 'Not a Match':
              if (value !== '') return false;
              break;

            case 'Is provided':
              if (value === '') return false;
              break;
            case 'Is not provided':
              if (value !== '') return false;
              break;

            /** ✅ Text filters */
            case 'Contains': {
              const keyword = filters[filterName + '_input'] || '';
              if (keyword && !value.toLowerCase().includes(keyword.toLowerCase())) return false;
              break;
            }
            case 'Not contains': {
              const keyword = filters[filterName + '_input'] || '';
              if (keyword && value.toLowerCase().includes(keyword.toLowerCase())) return false;
              break;
            }

            /** ✅ Numeric filters */
            case 'Superior than': {
              const num = Number(filters[filterName + '_input']);
              if (!isNaN(num) && Number(value) <= num) return false;
              break;
            }
            case 'Inferior than': {
              const num = Number(filters[filterName + '_input']);
              if (!isNaN(num) && Number(value) >= num) return false;
              break;
            }

            /** ✅ Duplicates */
            case 'Is duplicate':
              if (value.toLowerCase() !== 'yes') return false;
              break;
            case 'Is not duplicate':
              if (value.toLowerCase() === 'yes') return false;
              break;

            /** ✅ Inbox / Pipeline */
            case 'Is added':
              if (value.toLowerCase() !== 'yes') return false;
              break;
            case 'Is not added':
              if (value.toLowerCase() === 'yes') return false;
              break;

            /** ✅ LinkedIn account */
            case 'Is open profile':
              if (value.toLowerCase() !== 'open') return false;
              break;
            case 'Is not open profile':
              if (value.toLowerCase() === 'open') return false;
              break;
            case 'Has profile picture':
              if (value.toLowerCase() !== 'yes') return false;
              break;
            case 'Has not profile picture':
              if (value.toLowerCase() === 'yes') return false;
              break;
            case 'Is open to work':
              if (value.toLowerCase() !== 'yes') return false;
              break;
            case 'Is no open to work':
              if (value.toLowerCase() === 'yes') return false;
              break;
            case 'Is premium':
              if (value.toLowerCase() !== 'yes') return false;
              break;
            case 'Is not premium':
              if (value.toLowerCase() === 'yes') return false;
              break;

            /** ✅ Connection / Conversation */
            case 'Is a connection':
              if (value.toLowerCase() !== 'connection') return false;
              break;
            case 'Is not a connection':
              if (value.toLowerCase() === 'connection') return false;
              break;
            case 'Has a conversation':
              if (value.toLowerCase() !== 'conversation') return false;
              break;
            case 'Has not a conversation':
              if (value.toLowerCase() === 'conversation') return false;
              break;

            default:
              break;
          }
        }

        return true;
      });
    });

    return rows;
  }, [csvRows, filters, form, csvColumns]);

  const { csv_rows, totalCount } = useMemo(() => {
    return {
      csv_rows: filteredRows?.map((row, index) => ({
        // build cells array dynamically using columnNames order
        cells: columnNames.map(col => row[col] || '------------'),

        // keep original object if needed
        original: row,

        // optional id if you want unique keys
        id: index + 1,
      })),

      totalCount: filteredRows?.length,
    };
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return csv_rows?.slice(start, start + pageSize) || [];
  }, [csv_rows, currentPage, pageSize]);

  return (
    <>
      <TasksPreviewStyle>
        <div className="header">
          <div className="heading-box">
            <span className="btn-back" onClick={() => window.history.back()}>
              <GoArrowLeft />
            </span>
            <strong className="heading">
              Cold Outreach <span>(1868 Leads)</span>
            </strong>
            <span className="text">Extracted from Sales Navigator on February 27, 2025.</span>
          </div>
          <div className="btn-box">
            <Button>
              <img src={iconDownload} width="18" alt="download" />
              Download with filters
            </Button>
          </div>
        </div>
        <div className="content-holder">
          <aside className="sidebar">
            <Form form={form} style={{ marginBottom: '10px' }}>
              <Form.Item
                type="search"
                rounded
                noMargin
                name="search"
                placeholder="Search by name"
                prefix={<FiSearch size={22} />}>
                <Field />
              </Form.Item>
            </Form>
            <span className="filter-text">Filters:</span>

            {filterList?.map((filter, index) => (
              <FilterSelect
                key={filter.title}
                title={filter.title}
                selectedOptions={filters[filter.title] || []}
                options={filter.options}
                isInput={filter.isInput}
                onChange={newArray => setFilters(prev => ({ ...prev, [filter.title]: newArray }))}
              />
            ))}
          </aside>
          <div className="table-holder">
            <div className="scroll-content">
              <TableLayout
                noStyle
                onChangeFilters={({ page }) => {
                  if (page) setCurrentPage(page);
                }}
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}>
                <Table
                  rowsData={paginatedRows}
                  loading={csvLoading}
                  loadingSkeletonLength={14}
                  columnNames={columnNames}
                  noPadding
                  spanOnTd
                />
              </TableLayout>
            </div>
          </div>
        </div>
      </TasksPreviewStyle>
    </>
  );
};

export default TasksPreviewSection;
